"use client";

import type { DelegationIdentity } from "@icp-sdk/core/identity";
import { TrendingUp } from "lucide-react";
import dynamic from "next/dynamic";
import React, { useState } from "react";
import LuckyGiftBanner from "@/app/plaza/components/LuckyGiftBanner";
import OdinForgeBanner from "@/app/plaza/components/OdinForgeBanner";
import { SponsoredRankingBanner } from "@/app/plaza/components/sponsored-ranking-banner";
import SignInModal from "@/components/auth/sign-in-modal";
import MainLayout from "@/components/layout/main-layout";
import { FeaturedBuildersSection } from "@/app/plaza/components/featured-builders-section";
import HotTokensList from "@/components/shared/hot-tokens-list";
import { Card, CardContent } from "@/components/ui/card";
import { useIdentity } from "@/contexts/IdentityContext";
import { UserInfoProvider } from "@/contexts/UserInfoContext";
import { useLanguage } from "@/hooks/useLanguage";

// 静态导入核心侧边栏
import { SidebarFavorites } from "./components/sidebar-favorites";
import { SidebarHotMedia } from "./components/sidebar-hot-media";

// 动态导入重度数据组件，不阻塞首屏渲染
const CommunityActivity = dynamic(
	() => import("./components/community-activity").then(mod => mod.CommunityActivity),
	{ ssr: false, loading: () => <div className="h-[400px] w-full bg-light-surface dark:bg-dark-surface animate-pulse rounded-2xl" /> }
);

const TwitterUpdates = dynamic(
	() => import("./components/twitter-updates").then(mod => mod.TwitterUpdates),
	{ ssr: false, loading: () => <div className="h-[400px] w-full bg-light-surface dark:bg-dark-surface animate-pulse rounded-2xl" /> }
);

export default function PlazaPage() {
	const { setAuthData } = useIdentity();
	const { getTranslation, language } = useLanguage();
	const [showSignInModal, setShowSignInModal] = useState(false);

	const handleSignInSuccess = (
		newPrincipal: string,
		address: string,
		newIdentity: DelegationIdentity,
	) => {
		setAuthData(newPrincipal, address, newIdentity);
		setShowSignInModal(false);
	};

	return (
		<MainLayout>
			<UserInfoProvider>
				<div className="min-h-screen bg-light-background dark:bg-dark-background">
					{/* 顶栏与搜索 */}
						<div className="w-full">
							<div className="max-w-6xl mx-auto px-6 pt-6">
								<SponsoredRankingBanner className="mb-2" />
							</div>
						</div>

					{/* 统一顶部搜索已接管，此处暂时隐藏页面内搜索条 */}

					<SignInModal
						open={showSignInModal}
						onOpenChange={setShowSignInModal}
						onSuccess={handleSignInSuccess}
					/>

					{/* 主内容区域 */}
					<div className="max-w-6xl mx-auto p-4 grid grid-cols-1 lg:grid-cols-4 gap-6">
						{/* 侧边栏：核心信息，静态/轻量加载 */}
						<div className="hidden lg:block lg:col-span-1 space-y-6">
							<LuckyGiftBanner language={language} />
							<OdinForgeBanner language={language} />
							<SidebarFavorites />
							<SidebarHotMedia />
						</div>

						{/* 主栏：重度交互，异步加载 */}
						<div className="hidden lg:block lg:col-span-3 space-y-6">
							<FeaturedBuildersSection />
							
							<Card className="rounded-2xl bg-light-surface dark:bg-dark-surface border-light-border dark:border-dark-border shadow-md">
								<CardContent className="p-6">
									<div className="flex items-center justify-between mb-4">
										<h3 className="font-bold text-light-text dark:text-dark-text flex items-center gap-2">
											<TrendingUp className="h-5 w-5 text-light-accent dark:text-dark-accent" />
											{getTranslation("plaza.hotTokens")}
										</h3>
										<div className="flex items-center gap-4 text-xs text-gray-500">
											<div className="flex items-center gap-1">
												<div className="w-2 h-2 bg-red-500 rounded-full"></div>
												<span>{getTranslation("plaza.redFlashSell")}</span>
											</div>
											<div className="flex items-center gap-1">
												<div className="w-2 h-2 bg-blue-500 rounded-full"></div>
												<span>{getTranslation("plaza.blueFlashBuy")}</span>
											</div>
										</div>
									</div>
									<HotTokensList />
								</CardContent>
							</Card>

							{/* 社区动态：懒加载 */}
							<CommunityActivity />

							{/* 推特更新：懒加载 */}
							<TwitterUpdates />
						</div>

						{/* 移动端适配 */}
						<div className="block lg:hidden space-y-6">
							<Card className="rounded-2xl bg-light-surface dark:bg-dark-surface border-light-border dark:border-dark-border shadow-md">
								<CardContent className="p-6">
									<div className="mb-4">
										<h3 className="font-bold text-light-text dark:text-dark-text flex items-center gap-2 mb-2">
											<TrendingUp className="h-5 w-5 text-light-accent dark:text-dark-accent" />
											{getTranslation("plaza.hotTokens")}
										</h3>
										<div className="flex items-center gap-3 text-xs text-gray-500">
											<div className="flex items-center gap-1">
												<div className="w-2 h-2 bg-red-500 rounded-full"></div>
												<span>{getTranslation("plaza.redFlashSell")}</span>
											</div>
											<div className="flex items-center gap-1">
												<div className="w-2 h-2 bg-blue-500 rounded-full"></div>
												<span>{getTranslation("plaza.blueFlashBuy")}</span>
											</div>
										</div>
									</div>
									<HotTokensList />
								</CardContent>
							</Card>

							<FeaturedBuildersSection />
							<LuckyGiftBanner language={language} />
							<OdinForgeBanner language={language} />
							<CommunityActivity />
							<SidebarFavorites />
							<TwitterUpdates />
						</div>
					</div>
				</div>
			</UserInfoProvider>
		</MainLayout>
	);
}
